import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';

import { takeNext } from '@/lib/auth/next';
import { useRequireAuth } from '@/lib/auth/useRequireAuth';
import { supabase } from '@/lib/supabase';

const applicationSchema = z.object({
  fullName: z.string().min(2, 'Required'),
  background: z
    .string()
    .min(40, 'A short paragraph helps editorial decide. Aim for 2–4 sentences.'),
  intendedFocus: z.string().min(20, 'Briefly: which traditions or topics will you mostly cover?'),
  sampleWorkUrls: z
    .array(
      z.object({
        url: z.string().url('Each entry must be a valid URL').or(z.literal('')),
      }),
    )
    .max(5, 'Up to five links'),
  accept: z.literal(true, { errorMap: () => ({ message: 'Required' }) }),
});
type ApplicationValues = z.infer<typeof applicationSchema>;

export const Route = createFileRoute('/onboarding/creator')({
  component: CreatorOnboardingRoute,
});

function CreatorOnboardingRoute() {
  const { user, profile, refreshProfile } = useRequireAuth();
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<ApplicationValues>({
    resolver: zodResolver(applicationSchema),
    defaultValues: {
      fullName: profile?.display_name ?? '',
      background: '',
      intendedFocus: '',
      sampleWorkUrls: [{ url: '' }],
      accept: false as unknown as true,
    },
  });
  const { fields, append, remove } = useFieldArray({
    name: 'sampleWorkUrls',
    control: form.control,
  });

  const onSubmit = form.handleSubmit(async (values) => {
    if (!user) return;
    setSubmitError(null);
    try {
      const urls = values.sampleWorkUrls.map((s) => s.url).filter((s) => s.length > 0);
      const { error: insertError } = await supabase.from('creator_applications').insert({
        user_id: user.id,
        full_name: values.fullName,
        background: values.background,
        intended_focus: values.intendedFocus,
        sample_work_urls: urls,
      });
      if (insertError) throw insertError;
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          creator_status: 'pending',
          onboarding_completed_at: new Date().toISOString(),
        })
        .eq('id', user.id);
      if (profileError) throw profileError;
      await refreshProfile();
      // Creator applicants land on application-status; a carried next is out of
      // scope here (Spec 06 confirm 3), so consume-and-drop it rather than leak.
      takeNext();
      void navigate({ to: '/creator/application-status' });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Unable to submit application');
    }
  });

  const marketingUrl = import.meta.env.PUBLIC_MARKETING_URL ?? 'https://archetelos.stream';

  return (
    <div className="min-h-screen bg-parchment">
      <main className="mx-auto max-w-2xl px-6 py-16">
        <h1 className="font-serif text-3xl text-ink">Apply as a contributor.</h1>
        <p className="mt-3 font-sans text-base text-ink/70 pretty">
          Editorial reviews every application. A response usually arrives within a week. Approved
          contributors can submit essays and video; all work is reviewed before publication.
        </p>

        <form onSubmit={onSubmit} noValidate className="mt-10 space-y-6">
          <Field
            id="fullName"
            label="Full name"
            autoComplete="name"
            error={form.formState.errors.fullName?.message}
            {...form.register('fullName')}
          />

          <Textarea
            id="background"
            label="Background and credentials"
            rows={5}
            placeholder="Where you've published, taught, studied, or worked in this material."
            error={form.formState.errors.background?.message}
            {...form.register('background')}
          />

          <Textarea
            id="intendedFocus"
            label="Intended focus"
            rows={3}
            placeholder="Which traditions and topics do you plan to write or film about?"
            error={form.formState.errors.intendedFocus?.message}
            {...form.register('intendedFocus')}
          />

          <div>
            <p className="mb-1 block font-sans text-xs uppercase tracking-[0.12em] text-ink/60">
              Sample work (optional, up to 5)
            </p>
            <div className="space-y-2">
              {fields.map((field, index) => (
                <div key={field.id} className="flex items-start gap-2">
                  <input
                    type="url"
                    placeholder="https://"
                    {...form.register(`sampleWorkUrls.${index}.url`)}
                    className="w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none focus:ring-2 focus:ring-oxblood/30"
                  />
                  {fields.length > 1 && (
                    <button
                      type="button"
                      onClick={() => remove(index)}
                      className="text-xs text-ink/50 hover:text-oxblood"
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
            </div>
            {fields.length < 5 && (
              <button
                type="button"
                onClick={() => append({ url: '' })}
                className="mt-2 text-xs text-oxblood hover:underline"
              >
                + Add another link
              </button>
            )}
            {form.formState.errors.sampleWorkUrls && (
              <p role="alert" className="mt-1 text-xs text-signal">
                {form.formState.errors.sampleWorkUrls.message ?? 'Each entry must be a valid URL.'}
              </p>
            )}
          </div>

          <label className="flex items-start gap-2 text-sm text-ink/80">
            <input
              type="checkbox"
              {...form.register('accept')}
              className="mt-1 h-4 w-4 rounded border-ink/30 text-oxblood focus:ring-oxblood"
            />
            <span>
              I&apos;ve read and agree to the{' '}
              <a
                href={`${marketingUrl}/legal/contributor-agreement`}
                target="_blank"
                rel="noreferrer"
                className="text-oxblood hover:underline"
              >
                Contributor Agreement
              </a>
              , including the editorial standards in Section 1.
            </span>
          </label>
          {form.formState.errors.accept && (
            <p role="alert" className="text-xs text-signal">
              {form.formState.errors.accept.message}
            </p>
          )}

          {submitError && (
            <p role="alert" aria-live="polite" className="text-sm text-signal">
              {submitError}
            </p>
          )}

          <button
            type="submit"
            disabled={form.formState.isSubmitting}
            className="w-full rounded-md bg-ink px-4 py-2.5 font-sans text-sm font-medium text-parchment hover:bg-oxblood disabled:opacity-60"
          >
            {form.formState.isSubmitting ? 'Submitting…' : 'Submit application'}
          </button>
        </form>
      </main>
    </div>
  );
}

interface FieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  id: string;
  label: string;
  error?: string;
}
const Field = ({ id, label, error, ...rest }: FieldProps) => (
  <div>
    <label
      htmlFor={id}
      className="mb-1 block font-sans text-xs uppercase tracking-[0.12em] text-ink/60"
    >
      {label}
    </label>
    <input
      id={id}
      {...rest}
      aria-invalid={Boolean(error)}
      className="w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none focus:ring-2 focus:ring-oxblood/30"
    />
    {error && (
      <p role="alert" className="mt-1 text-xs text-signal">
        {error}
      </p>
    )}
  </div>
);

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  id: string;
  label: string;
  error?: string;
}
const Textarea = ({ id, label, error, ...rest }: TextareaProps) => (
  <div>
    <label
      htmlFor={id}
      className="mb-1 block font-sans text-xs uppercase tracking-[0.12em] text-ink/60"
    >
      {label}
    </label>
    <textarea
      id={id}
      {...rest}
      aria-invalid={Boolean(error)}
      className="w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none focus:ring-2 focus:ring-oxblood/30"
    />
    {error && (
      <p role="alert" className="mt-1 text-xs text-signal">
        {error}
      </p>
    )}
  </div>
);
